// lib/strategyApi.js — Phase 3B dedicated axios instance for the
// individual-account auth + persisted-project endpoints
// (`/admin/accounts/*`, `/admin/planification/projects/*`).
//
// Deliberately NOT sharing `lib/api.js`'s `api` instance: that
// instance's CSRF interceptor picks the LEGACY `rr_admin_csrf` cookie
// for any `/admin/` path, and its 401 handler clears the legacy
// `adminAuth` session — both wrong for this module. A fully separate
// instance means zero risk of regressing the legacy admin/agency CSRF
// or session-clearing behaviour.
import axios from "axios";
import { strategyAuth } from "./strategyAuth";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const strategyApi = axios.create({
  baseURL: `${API_BASE}/api/admin`,
  withCredentials: true,
});

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : "";
};

strategyApi.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    config.headers["X-CSRF-Token"] = readCookie("rr_admin_acct_csrf");
  }
  return config;
});

strategyApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      // Only ever clears THIS module's own session state — never
      // touches the legacy admin panel's logged-in flag.
      strategyAuth.clear();
    }
    return Promise.reject(error);
  }
);

export const strategyEndpoints = {
  activate: (token, newPassword) => strategyApi.post("/accounts/activate", { token, new_password: newPassword }),
  mfaEnroll: (mfaSetupToken) => strategyApi.post("/accounts/mfa/enroll", { mfa_setup_token: mfaSetupToken }),
  mfaVerify: (mfaSetupToken, totpCode) => strategyApi.post("/accounts/mfa/verify", { mfa_setup_token: mfaSetupToken, totp_code: totpCode }),
  login: (email, password, totpCode, countryCode) =>
    strategyApi.post("/accounts/login", { email, password, totp_code: totpCode, country_code: countryCode }),
  loginRecovery: (email, password, recoveryCode, countryCode) =>
    strategyApi.post("/accounts/login/recovery", { email, password, recovery_code: recoveryCode, country_code: countryCode }),
  logout: () => strategyApi.post("/accounts/logout"),
  me: () => strategyApi.get("/accounts/me"),
  switchCountry: (countryCode) => strategyApi.post("/accounts/switch-country", { country_code: countryCode }),
  createAccount: (email, countryCode, role) => strategyApi.post("/accounts/create", { email, country_code: countryCode, role }),
  addCountryMembership: (targetAccountId, countryCode, role) =>
    strategyApi.post("/accounts/add-country-membership", { target_account_id: targetAccountId, country_code: countryCode, role }),
  suspendAccount: (targetAccountId, countryCode, reason) =>
    strategyApi.post("/accounts/suspend", { target_account_id: targetAccountId, country_code: countryCode, reason }),
  reactivateAccount: (targetAccountId, countryCode) =>
    strategyApi.post("/accounts/reactivate", { target_account_id: targetAccountId, country_code: countryCode }),
  assignRole: (targetAccountId, countryCode, newRole) =>
    strategyApi.post("/accounts/assign-role", { target_account_id: targetAccountId, country_code: countryCode, new_role: newRole }),
  revokeInvitation: (targetAccountId, countryCode) =>
    strategyApi.post("/accounts/revoke-invitation", { target_account_id: targetAccountId, country_code: countryCode }),
  auditLog: (countryCode) => strategyApi.get(`/accounts/audit/${countryCode}`),

  listProjects: () => strategyApi.get("/planification/projects"),
  createProject: (name, description) => strategyApi.post("/planification/projects", { name, description }),
  getProject: (projectId) => strategyApi.get(`/planification/projects/${projectId}`),
  archiveProject: (projectId) => strategyApi.delete(`/planification/projects/${projectId}`),
  shareProject: (projectId, targetAccountId, role) =>
    strategyApi.post(`/planification/projects/${projectId}/share`, { target_admin_account_id: targetAccountId, role }),
  revokeShare: (projectId, targetAccountId) =>
    strategyApi.post(`/planification/projects/${projectId}/revoke-share`, { target_admin_account_id: targetAccountId }),
  transferOwnership: (projectId, newOwnerAdminId, reason) =>
    strategyApi.post(`/planification/projects/${projectId}/transfer-ownership`, { new_owner_admin_id: newOwnerAdminId, reason }),
  saveVersion: (projectId, expectedVersionSeq, objects, label) =>
    strategyApi.post(`/planification/projects/${projectId}/versions`, { expected_version_seq: expectedVersionSeq, objects, label }),
  listVersions: (projectId) => strategyApi.get(`/planification/projects/${projectId}/versions`),
  restoreVersion: (projectId, sourceVersionId, expectedVersionSeq) =>
    strategyApi.post(`/planification/projects/${projectId}/versions/restore`, { source_version_id: sourceVersionId, expected_version_seq: expectedVersionSeq }),
  exportProject: (projectId) => strategyApi.get(`/planification/projects/${projectId}/export`),
  importProject: (projectId, expectedVersionSeq, layerId, featureCollection) =>
    strategyApi.post(`/planification/projects/${projectId}/import`, { expected_version_seq: expectedVersionSeq, layer_id: layerId, feature_collection: featureCollection }),
};
